import type { ReactElement, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import type { AnimationProps } from 'framer-motion';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { CurtainReveal } from '@frameio/shared/components/CurtainReveal';
import type { UseVirtualizerOpts } from '@frameio/vapor';
import {
  ScrollContainer,
  theme,
  useIsomorphicLayoutEffect,
  useVirtualizer,
} from '@frameio/vapor';
import type { UseScrollBoundaryOpts } from '@frameio/vapor-migration';
import { useScrollBoundary } from '@frameio/vapor-migration';
import { LIST_HEIGHT } from '../constants';

// Copied and slightly adjusted from Dave's FixedHeightList story: @frameio/vapor/packages/vapor/src/hooks/useVirtualizer/stories/FixedHeightList/FixedHeightList.tsx

type VirtualListProps = {
  /** our list of props , the entries of which are passed to the children render prop */
  list: any[];
  /** fixed item height */
  itemHeight: number;
  /** scroll to index */
  scrollToIndex?: number;
  /** margin from bottom or top to trigger boundary events */
  scrollBoundaryMargin?: number;
  /** render prop child called with props from list and the associated index */
  children: (props: any, index: number) => ReactElement<any>;
  /** full list animation framer-motion props */
  animation?: AnimationProps;
  /** optional children to render after the list */
  footer?: ReactNode;
} & Omit<UseVirtualizerOpts, 'listLength'> &
  Omit<UseScrollBoundaryOpts, 'margin'>;

export const SearchResultsList = ({
  list,
  children,
  overscan,
  itemHeight,
  scrollToIndex,
  scrollBoundaryMargin,
  onBoundary,
  onScrollChanged,
  animation,
  footer,
}: VirtualListProps) => {
  const {
    viewportRef: virtualizerVpRef,
    top,
    count,
    listHeight,
    topIndex,
    viewportHeight,
    scrollTop,
  } = useVirtualizer({
    overscan,
    itemHeight,
    listLength: list.length,
    defaultViewportHeight: LIST_HEIGHT,
  }) ?? {};

  const viewportRef = useRef<HTMLDivElement>(undefined);

  /** render new children only if the slice of the list has changed */
  const memoizedChildren = useMemo(() => {
    if (topIndex === undefined || topIndex === null || count === undefined)
      return null;
    return list.slice(topIndex, topIndex + count).map((_, i) => {
      const listIndex = i + topIndex;
      const item = list[listIndex];
      const result = item.result;
      return children(result, listIndex);
    });
  }, [list, topIndex, count, children]);

  /** trigger scrollTo on the viewport when scrollToIndex changes */
  useIsomorphicLayoutEffect(() => {
    if (
      scrollToIndex !== undefined &&
      scrollToIndex >= 0 &&
      viewportRef?.current
    ) {
      const itemDistanceFromListTop = itemHeight * scrollToIndex;
      const itemDistanceFromViewportTop =
        itemDistanceFromListTop - (scrollTop ?? 0);

      // accounting for items cut off, even by 1 pixel
      const bottomHeightForItemsFullyInView = viewportHeight
        ? viewportHeight - itemHeight + 1
        : 0;
      const topHeightForItemsFullyInView = (scrollTop ?? 0) + itemHeight - 1;

      const itemIsBelowViewport =
        // on first render, bottomHeightForItemsFullyInView will be negative
        bottomHeightForItemsFullyInView > 0 &&
        itemDistanceFromViewportTop > bottomHeightForItemsFullyInView;
      const itemIsAboveViewport =
        itemDistanceFromListTop < topHeightForItemsFullyInView;

      // mimics most scrolling via keyboard where if item is above visible list,
      // the scroll will position that item at the top and vice versa for the
      // bottom, moving one item into the visible list at a time
      if (itemIsAboveViewport || scrollToIndex === list.length - 1) {
        viewportRef.current.scrollTo({
          behavior: `smooth`,
          top: itemDistanceFromListTop,
        });
      } else if (itemIsBelowViewport) {
        viewportRef.current.scrollTo({
          behavior: `smooth`,
          top: viewportHeight
            ? itemDistanceFromListTop - (viewportHeight - itemHeight)
            : 0,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  /** listen for scroll boundary events */
  useScrollBoundary(
    viewportRef.current?.scrollTop ?? 0,
    viewportRef.current?.scrollHeight ?? 0,
    viewportRef.current?.clientHeight ?? 0,
    {
      margin: scrollBoundaryMargin,
      onBoundary,
      onScrollChanged,
    }
  );

  return (
    <Viewport
      ref={(r) => {
        if (!r) return;
        virtualizerVpRef?.(r);
        viewportRef.current = r;
      }}
      data-testid="quick-search-results-list"
      paddingX={0}
    >
      <motion.ul
        style={{ height: listHeight, paddingTop: top ?? 0 }}
        {...animation}
      >
        {memoizedChildren}
        {footer}
      </motion.ul>
      <CurtainReveal
        bufferTime={0}
        color={theme.colors.components.modal.background}
        hold={list.length === 0}
        id="search-list"
        orientation="vertical"
        zIndex={theme.zIndices.modal}
      />
    </Viewport>
  );
};

const Viewport = styled(ScrollContainer)`
  width: 100%;

  ${theme.mediaQueries.tablet} {
    width: 50%;
  }
`;
